import { ActiveFiltersSummaryList } from "@/components/data-table/components/ActiveFiltersSummary/ActiveFiltersSummaryList";
import type { ActiveFilterDescriptor } from "@/components/data-table/utils/activeFilterDescriptors";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { FilterIcon } from "lucide-react";
import { memo } from "react";

import { ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS } from "./activeFiltersBarShared";

interface ActiveFiltersBarDescriptorSlotProps {
  segmentClass: string;
  idleLabelClass: string;
  triggerInteractionsClass: string;
  activeGlyphClass: string;
  countLabel: string;
  noneLabel: string;
  descriptors: ActiveFilterDescriptor[];
  onRemoveColumnFilter: (columnId: string) => void;
  onClearAll: () => void;
  getColumnVisibilityToggle?: (
    columnId: string,
  ) => { isVisible: boolean; onToggle: () => void } | undefined;
}

const ActiveFiltersBarDescriptorSlotComp = ({
  segmentClass,
  idleLabelClass,
  triggerInteractionsClass,
  activeGlyphClass,
  countLabel,
  noneLabel,
  descriptors,
  onRemoveColumnFilter,
  onClearAll,
  getColumnVisibilityToggle,
}: ActiveFiltersBarDescriptorSlotProps) => {
  const isMobile = useIsMobile();
  const committedCount = descriptors.length;
  const hasDescriptors = committedCount > 0;

  if (!hasDescriptors) {
    return (
      <span
        className={cn(
          segmentClass,
          idleLabelClass,
          ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
        )}
      >
        <FilterIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span className="hidden sm:inline">{noneLabel}</span>
        <span className="sr-only sm:hidden">{noneLabel}</span>
      </span>
    );
  }

  const filterSummaryList = (
    <ActiveFiltersSummaryList
      descriptors={descriptors}
      onRemoveColumnFilter={onRemoveColumnFilter}
      onClearAll={onClearAll}
      getColumnVisibilityToggle={getColumnVisibilityToggle}
    />
  );

  const triggerButton = (
    <button
      type="button"
      className={cn(
        segmentClass,
        ACTIVE_FILTERS_BAR_SEGMENT_INNER_PAD_CLASS,
        "text-left outline-none transition-colors",
        triggerInteractionsClass,
      )}
    >
      <FilterIcon className={cn(activeGlyphClass, "h-3.5 w-3.5 shrink-0")} />
      <span className="tabular-nums sm:hidden">{committedCount}</span>
      <span className="hidden min-w-0 truncate underline decoration-dotted underline-offset-2 sm:inline">
        {countLabel}
      </span>
    </button>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{triggerButton}</SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            "flex h-auto max-h-[min(85dvh,560px)] flex-col gap-0 overflow-hidden rounded-t-xl border-0 p-0 pb-[env(safe-area-inset-bottom)]",
            "[&>button.absolute]:right-3 [&>button.absolute]:top-3.5",
          )}
        >
          <div className="shrink-0 border-b px-4 pb-3 pt-4 pr-12">
            <SheetTitle className="text-left text-base font-medium leading-tight">
              {countLabel}
            </SheetTitle>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {filterSummaryList}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-[min(100vw-2rem,20rem)] p-2" align="start">
        {filterSummaryList}
      </PopoverContent>
    </Popover>
  );
};

export const ActiveFiltersBarDescriptorSlot = memo(
  ActiveFiltersBarDescriptorSlotComp,
);
